import { useForm } from "react-hook-form";
import { SubmitButton } from "./submit_button";
import { capitalize } from "lodash";
import { useDevToolForm } from "./dev_tool";
import { useEffect } from "react";

const formatOptions = (options: string[] | Record<string, string>) => {
  if (Array.isArray(options)) {
    return options.map((option) => {
      if (typeof option === "string" || typeof option === "number") {
        return { label: capitalize(option), value: option };
      }
      return option;
    });
  }
  return Object.entries(options).map(([value, label]) => ({ value, label }));
};

type FieldConfig = {
  type?: string;
  required?: boolean;
  pattern?: string;
  patternMessage?: string;
  label?: string;
  options?: string[] | Record<string, string>;
  placeholder?: string | boolean;
  helper?: (value: any, data: any) => React.ReactNode;
  if?: (values: Record<string, any>) => boolean;
};

export const SimpleForm = ({
  fields,
  data = {},
  onSubmit,
  saving,
  errors: externalErrors = {},
  devToolValues = {},
  submitButtonText = "Save",
  className,
  inlineActions,
}: {
  fields: Record<string, FieldConfig>;
  data?: Record<string, any>;
  onSubmit: (data: Record<string, any>) => void;
  saving?: boolean;
  errors?: Record<string, any>;
  devToolValues?: Record<string, any>;
  submitButtonText?: string;
  className?: string;
  inlineActions?: React.ReactNode;
}) => {
  const { register, handleSubmit, formState, setValue, getValues, watch, reset } =
    useForm({
      defaultValues: data,
    });

  if (devToolValues) {
    useDevToolForm(devToolValues, setValue, getValues);
  }

  const watchedValues = watch();

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={className}>
      <div className="mb-3">
        {Object.entries(fields).map(([key, config]) => {
          const error = formState.errors[key]?.message || externalErrors[key];

          // Check if field should be shown based on "if" condition
          if (config.if && !config.if(watchedValues)) {
            return null;
          }

          // Create validation rules object
          const validationRules: any = {};
          if (config.required) {
            validationRules.required = "This field is required";
          }
          if (config.pattern) {
            validationRules.pattern = {
              value: config.pattern,
              message: config.patternMessage || "Invalid format",
            };
          }

          // Determine placeholder text
          let placeholderText: string | undefined = undefined;
          if (typeof config.placeholder === "string") {
            placeholderText = config.placeholder;
          } else if (config.placeholder === true) {
            placeholderText = config.label ?? key.replace(/_/g, " ");
          }

          return (
            <div key={key} className="mb-3" data-field={key}>
              <label className="fw-bold text-capitalize">
                {config.label ?? key.replace(/_/g, " ")}
                {config.required && <span className="text-danger ml-1 fs-14">*</span>}
              </label>
              <div className="mt-1">
                {config.type === "json" ? (
                  <pre className="fs-12">{JSON.stringify(data[key], null, 2)}</pre>
                ) : config.type === "select" ? (
                  <select
                    {...register(key, validationRules)}
                    className={`form-select ${error ? "is-invalid" : ""}`}
                  >
                    {config.placeholder !== false && (
                      <option value="">
                        {typeof config.placeholder === "string"
                          ? config.placeholder
                          : config.label ?? key.replace(/_/g, " ")}
                      </option>
                    )}
                    {formatOptions(config.options || []).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                ) : config.type === "textarea" ? (
                  <textarea
                    {...register(key, validationRules)}
                    className={`form-control ${error ? "is-invalid" : ""}`}
                    placeholder={placeholderText}
                  />
                ) : (
                  <input
                    {...register(key, validationRules)}
                    type={config.type || "text"}
                    className={`form-control ${error ? "is-invalid" : ""}`}
                    placeholder={placeholderText}
                  />
                )}
                {config.helper && config.helper(watch(key), watchedValues)}
                {error && <div className="invalid-feedback d-block">{error}</div>}
              </div>
            </div>
          );
        })}
      </div>
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="">{inlineActions}</div>
        <SubmitButton type="submit" className="btn btn-primary" loading={saving}>
          {submitButtonText}
        </SubmitButton>
      </div>
    </form>
  );
};
